import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Pencil, Plus, Eye, EyeOff } from "lucide-react";

const EMPTY = {
  name: "", slug: "", description: "", course_ids: [],
  pricing_type: "percent", fixed_price_usd: 79, percent_off: 25, is_published: true,
};

export default function AdminBundles() {
  const [list, setList] = useState([]);
  const [courses, setCourses] = useState([]);
  const [editing, setEditing] = useState(null);   // null or bundle obj
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/api/admin/bundles").then(({data}) => setList(data));
  useEffect(() => {
    load();
    api.get("/api/admin/courses").then(({data}) => setCourses(data));
  }, []);

  const startEdit = (b) => {
    setEditing(b);
    setForm({
      name: b.name, slug: b.slug, description: b.description,
      course_ids: b.course_ids, pricing_type: b.pricing_type,
      fixed_price_usd: b.fixed_price_usd, percent_off: b.percent_off,
      is_published: b.is_published,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const reset = () => { setEditing(null); setForm(EMPTY); };

  const save = async (e) => {
    e.preventDefault();
    if (form.course_ids.length < 2) return toast.error("Pick at least 2 courses");
    try {
      if (editing) {
        await api.put(`/api/admin/bundles/${editing.id}`, form);
        toast.success("Bundle updated");
      } else {
        await api.post("/api/admin/bundles", form);
        toast.success("Bundle created");
      }
      reset(); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const remove = async (b) => {
    if (!window.confirm(`Delete bundle ${b.name}?`)) return;
    await api.delete(`/api/admin/bundles/${b.id}`);
    toast.success("Deleted"); load();
  };

  const toggle = async (b) => {
    await api.put(`/api/admin/bundles/${b.id}`, { is_published: !b.is_published });
    load();
  };

  return (
    <div data-testid="admin-bundles">
      <div className="label-eyebrow">Bundles</div>
      <h1 className="font-display font-black text-3xl mt-1">Course bundles</h1>
      <p className="text-sm text-[#4B5563] mt-1">Group multiple courses with a fixed price or a percentage discount.</p>

      <form onSubmit={save} className="mt-6 bg-white border border-[#E5E7EB] rounded-xl p-6 space-y-4" data-testid="bundle-form">
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <label className="text-sm font-semibold">Name</label>
            <input data-testid="bundle-name" required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})}
              placeholder="e.g. Job Ready in Canada" className="w-full mt-1 border border-[#E5E7EB] rounded-md px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="text-sm font-semibold">Slug (optional)</label>
            <input value={form.slug} onChange={(e) => setForm({...form, slug: e.target.value})}
              placeholder="auto-from-name" className="w-full mt-1 border border-[#E5E7EB] rounded-md px-3 py-2 text-sm font-mono text-xs" />
          </div>
        </div>
        <div>
          <label className="text-sm font-semibold">Description</label>
          <textarea rows={2} value={form.description} onChange={(e) => setForm({...form, description: e.target.value})}
            className="w-full mt-1 border border-[#E5E7EB] rounded-md px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="text-sm font-semibold">Courses ({form.course_ids.length} selected)</label>
          <div className="mt-1 max-h-64 overflow-y-auto border border-[#E5E7EB] rounded-md p-2 space-y-1">
            {courses.map((c) => (
              <label key={c.id} className="flex items-center gap-2 px-2 py-1 hover:bg-[#F3F4F6] rounded text-sm cursor-pointer">
                <input type="checkbox" checked={form.course_ids.includes(c.id)} data-testid={`bundle-course-${c.slug}`}
                  onChange={(e) => {
                    if (e.target.checked) setForm({...form, course_ids: [...form.course_ids, c.id]});
                    else setForm({...form, course_ids: form.course_ids.filter((x) => x !== c.id)});
                  }} />
                <span className="font-semibold flex-1">{c.title}</span>
                <span className="text-xs text-[#4B5563]">${c.price_usd}</span>
              </label>
            ))}
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-3">
          <div>
            <label className="text-sm font-semibold">Pricing</label>
            <select data-testid="bundle-pricing-type" value={form.pricing_type} onChange={(e) => setForm({...form, pricing_type: e.target.value})}
              className="w-full mt-1 border border-[#E5E7EB] rounded-md px-3 py-2 text-sm bg-white">
              <option value="percent">% Off combined</option>
              <option value="fixed">Fixed price</option>
            </select>
          </div>
          {form.pricing_type === "fixed" ? (
            <div>
              <label className="text-sm font-semibold">Fixed price (USD)</label>
              <input type="number" min={0} step={1} value={form.fixed_price_usd} onChange={(e) => setForm({...form, fixed_price_usd: parseFloat(e.target.value)})}
                className="w-full mt-1 border border-[#E5E7EB] rounded-md px-3 py-2 text-sm" />
            </div>
          ) : (
            <div>
              <label className="text-sm font-semibold">% Off combined</label>
              <input type="number" min={1} max={90} value={form.percent_off} onChange={(e) => setForm({...form, percent_off: parseFloat(e.target.value)})}
                className="w-full mt-1 border border-[#E5E7EB] rounded-md px-3 py-2 text-sm" />
            </div>
          )}
          <div className="flex items-center gap-2 mt-6">
            <input type="checkbox" id="pub" checked={form.is_published} onChange={(e) => setForm({...form, is_published: e.target.checked})} />
            <label htmlFor="pub" className="text-sm font-semibold">Published</label>
          </div>
        </div>
        <div className="flex justify-end gap-2">
          {editing && <button type="button" onClick={reset} className="btn-outline" data-testid="bundle-cancel">Cancel</button>}
          <button type="submit" data-testid="bundle-save" className="btn-primary flex items-center gap-1">
            <Plus className="w-4 h-4" /> {editing ? "Save changes" : "Create bundle"}
          </button>
        </div>
      </form>

      <div className="mt-6 bg-white border border-[#E5E7EB] rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F3F4F6]">
            <tr><th className="px-4 py-3 text-left">Name</th><th className="px-4 py-3 text-left">Courses</th><th className="px-4 py-3 text-left">Price</th><th className="px-4 py-3 text-left">You save</th><th className="px-4 py-3 text-left">Status</th><th className="px-4 py-3 text-right">Actions</th></tr>
          </thead>
          <tbody>
            {list.map((b) => (
              <tr key={b.id} className="border-t border-[#E5E7EB]">
                <td className="px-4 py-3 font-semibold">{b.name}</td>
                <td className="px-4 py-3">{b.courses.length}</td>
                <td className="px-4 py-3">${b.price_usd?.toFixed(2)} <span className="text-xs text-[#4B5563] line-through">${b.original_total?.toFixed(2)}</span></td>
                <td className="px-4 py-3 text-[#064E3B] font-semibold">${b.savings_usd?.toFixed(2)}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs font-bold uppercase px-2 py-1 rounded ${b.is_published ? "bg-[#064E3B]/10 text-[#064E3B]" : "bg-[#F3F4F6] text-[#4B5563]"}`}>
                    {b.is_published ? "Live" : "Draft"}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => toggle(b)} className="p-1.5 hover:bg-[#F3F4F6] rounded mr-1" title={b.is_published ? "Unpublish" : "Publish"} data-testid={`bundle-toggle-${b.slug}`}>
                    {b.is_published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                  <button onClick={() => startEdit(b)} className="p-1.5 hover:bg-[#F3F4F6] rounded mr-1" title="Edit" data-testid={`bundle-edit-${b.slug}`}>
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button onClick={() => remove(b)} className="p-1.5 hover:bg-[#FEE2E2] rounded" title="Delete" data-testid={`bundle-delete-${b.slug}`}>
                    <Trash2 className="w-4 h-4 text-[#DC2626]" />
                  </button>
                </td>
              </tr>
            ))}
            {list.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-[#4B5563]">No bundles yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
